import {
  Box,
  Heading,
  HeadingProps,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Stack,
  Text,
} from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import { Card, CardContent } from 'components/cards/Card';
import {
  BullhornIcon,
  CalendarAltIcon,
  EllipsisHIcon,
  FolderOpenIcon,
  InfoIcon,
  VaultIcon,
} from 'components/icons';
import { UserHandle } from 'components/UserHandle';
import React, { Suspense } from 'react';
import { generatePath } from 'react-router-dom';
import { graphql, useSafeRelay } from 'utils/graphClient';
import {
  CANONICAL_ANNOUNCEMENT_PATH,
  CANONICAL_ITEM_PATH,
  EVENT_PATH,
  VAULTS_MANAGER_VAULT_PATH,
} from 'utils/routes';
import { SpaceIndicator } from '../../../../components/SpaceIndicator';
import { DateTimeLabel } from '../../screens/Event/components/EventCard/DateTimeLabel';
import { TextLink } from '../TextLink';
import { ItemInfo_Item_Query } from './__generated__/ItemInfo_Item_Query.graphql';

const itemQuery = graphql`
  query ItemInfo_Item_Query($id: ID!) {
    item: node(id: $id) {
      __typename
      ... on FolderItem {
        event: ownershipEvent {
          id
          name
          ...DateTimeLabel_event
        }
        space: ownershipSpace {
          name
          ...SpaceIndicator_space
        }
        folders: ownershipFolders {
          nodes {
            id
            name
          }
        }
      }
      ... on VaultItem {
        vault {
          id
          name
        }
      }
      ... on AnnouncementAttachment {
        announcement {
          id
          announcer {
            ...UserHandle_member
          }
          space {
            ...SpaceIndicator_space
          }
        }
      }
      ... on MemberDetailsAttachment {
        memberDetails {
          id
          member {
            ...UserHandle_member
          }
        }
      }
    }
  }
`;

interface ItemInfoProps {
  nodeId: string;
  headingProps?: HeadingProps;
}

export const ItemInfo: React.FC<ItemInfoProps> = (props) => {
  const Loading = () => {
    return (
      <Box pos="relative">
        <Stack>
          <HStack>
            <Skeleton w={20} h={6} />
          </HStack>

          <Card isNotClickable>
            <CardContent>
              <Stack p={2}>
                <Skeleton w="full" h={6} />

                <Skeleton w="full" h={6} />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    );
  };

  return (
    <Suspense fallback={<Loading />}>
      <UnsuspendedItemContent {...props} />
    </Suspense>
  );
};

const UnsuspendedItemContent: React.FC<ItemInfoProps> = ({
  nodeId,
  headingProps = {
    size: 'sm',
    fontWeight: 'normal',
  },
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { item } = useLazyLoadQuery<ItemInfo_Item_Query>(itemQuery, {
    id: nodeId,
  });

  const renderContent = () => {
    switch (item?.__typename) {
      case 'AnnouncementAttachment': {
        const userName = (
          <UserHandle
            member={item.announcement.announcer}
            showAvatar
            linkToProfile
            avatarProps={{ size: 'xs' }}
          />
        );

        const spaceName = (
          <SpaceIndicator space={item.announcement.space} linkToSpace />
        );
        return (
          <HStack wrap="wrap">
            <BullhornIcon />

            <Trans comment="Announcement by {announcer} in {space}">
              <TextLink
                to={generatePath(CANONICAL_ANNOUNCEMENT_PATH, {
                  itemId: item.announcement.id,
                })}
              >
                <Text noOfLines={1}>{t`Announcement`}</Text>
              </TextLink>

              <Text>by</Text>

              {userName}

              <Text>on</Text>

              {spaceName}
            </Trans>
          </HStack>
        );
      }

      case 'FolderItem': {
        const folders = item.folders.nodes;
        const folder = item.folders.nodes[item.folders.nodes.length - 1];

        if (folder) {
          return (
            <Stack>
              <HStack spacing={folders.length > 1 ? 0 : 2}>
                <FolderOpenIcon />

                {folders.length > 1 ? (
                  <HStack spacing={0}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<EllipsisHIcon />}
                        variant="unstyled"
                      />
                      <MenuList>
                        {folders.slice(0, -1).map(({ name, id }) => (
                          <MenuItem key={id}>{name}</MenuItem>
                        ))}
                      </MenuList>
                    </Menu>

                    <HStack>
                      <Text>/</Text>

                      <Link
                        to={generatePath(CANONICAL_ITEM_PATH, {
                          itemId: folders[folders.length - 1].id,
                        })}
                      >
                        <Text fontWeight="bold" noOfLines={1}>
                          {folders[folders.length - 1]?.name}
                        </Text>
                      </Link>
                    </HStack>
                  </HStack>
                ) : (
                  <Link
                    to={generatePath(CANONICAL_ITEM_PATH, {
                      itemId: folder.id,
                    })}
                  >
                    <Text fontWeight="bold" noOfLines={1}>
                      {folder?.name}
                    </Text>
                  </Link>
                )}
              </HStack>

              <SpaceIndicator
                space={item.space}
                linkToSpace
                fontWeight="bold"
              />
            </Stack>
          );
        }

        if (item.event) {
          return (
            <Stack p={2}>
              <HStack>
                <CalendarAltIcon />

                <TextLink
                  fontWeight="bold"
                  noOfLines={1}
                  to={generatePath(EVENT_PATH, {
                    eventId: item.event.id,
                  })}
                >
                  {item.event.name}
                </TextLink>
              </HStack>

              <DateTimeLabel event={item.event} />
            </Stack>
          );
        }

        return (
          <SpaceIndicator space={item.space} linkToSpace fontWeight="bold" />
        );
      }

      case 'VaultItem':
        return (
          <HStack>
            <VaultIcon />

            <Link
              to={generatePath(VAULTS_MANAGER_VAULT_PATH, {
                vaultId: item.vault.id,
              })}
            >
              <Text fontWeight="bold" noOfLines={1}>
                {item.vault.name}
              </Text>
            </Link>
          </HStack>
        );

      case 'MemberDetailsAttachment':
        if (!item.memberDetails.member) return null;

        return (
          <UserHandle
            member={item.memberDetails.member}
            showAvatar
            linkToProfile
            avatarProps={{ size: 'xs' }}
          />
        );
      default:
        return null;
    }
  };

  const content = renderContent();

  if (!content) return null;

  return (
    <Box pos="relative">
      <HStack spacing={2}>
        <Icon as={InfoIcon} />

        <Heading {...headingProps}>{t`Context`}</Heading>
      </HStack>

      <Card isNotClickable>
        <CardContent>{content}</CardContent>
      </Card>
    </Box>
  );
};

export default ItemInfo;
